<template>
  <Form
    ref="formRef"
    :validation-schema="schema"
    :initial-values="props.initialValues"
    @submit="handleForm"
  >
    <div
      class="py-4 px-4 sm:px-8 bg-[#F9FAFC] w-full mt-8 flex flex-col gap-y-8 pt-8 rounded-md"
    >
      <!-- Personal Information Section -->
      <div>
        <h4 class="text-xl font-medium md:text-2xl">Personal Information</h4>
        <div class="w-full h-[1px] bg-gray-200"></div>
        <div class="grid gap-4 mt-2 md:grid-cols-2">
          <!-- Full name -->
          <div class="flex flex-col w-full gap-y-1">
            <label class="font-medium text-[#7B7979]" for="fullName">Full name</label>
            <Field
              name="fullName"
              v-slot="{ field, errors }"
              :validateOnInput="hasBeenSubmitted"
            >
              <input
                v-bind="field"
                type="text"
                placeholder="Insert your full name..."
                :class="{ 'ring-red-500 border-red-500': errors[0] }"
              />
            </Field>
            <ErrorMessage name="fullName" class="text-sm text-red-500" />
          </div>
          <!-- Email -->
          <div class="flex flex-col w-full gap-y-1">
            <label class="font-medium text-[#7B7979]" for="email">Email</label>
            <Field
              name="email"
              v-slot="{ field, errors, handleChange }"
              :validateOnInput="hasBeenSubmitted"
            >
              <input
                v-bind="field"
                type="text"
                placeholder="Insert an email..."
                :class="{ 'ring-red-500 border-red-500': errors[0] }"
                @blur="trimEmailField(field.value, handleChange)"
              />
            </Field>
            <ErrorMessage name="email" class="text-sm text-red-500" />
          </div>
          <!-- Contact number -->
          <div class="flex flex-col w-full gap-y-1">
            <label class="font-medium text-[#7B7979]" for="contactNumber"
              >Contact number</label
            >
            <IntlTelInput
              class="w-full"
              :class="{ 'ring-red-500 border-red-500': hasBeenSubmitted && !isPhoneValid }"
              @changeNumber="phoneNumber = $event"
              @changeValidity="isPhoneValid = $event"
              @changeErrorCode="phoneErrorCode = $event"
              :options="{
                initialCountry: 'us',
              }"
            />
            <span v-if="hasBeenSubmitted && !isPhoneValid" class="text-sm text-red-500">
              {{ getPhoneErrorMessage() }}
            </span>
          </div>

          <!-- Location -->
          <div class="flex flex-col w-full gap-y-1">
            <label class="font-medium text-[#7B7979]" for="location">Location</label>
            <Field
              name="location"
              v-slot="{ field, errors }"
              :validateOnInput="hasBeenSubmitted"
            >
              <div class="relative">
                <select
                  v-bind="field"
                  class="w-full appearance-none pr-8"
                  :class="{ 'ring-red-500 border-red-500': errors[0] }"
                  :disabled="loading"
                >
                  <option value="">Select a country...</option>
                  <option v-for="country in countries" :key="country" :value="country">
                    {{ country }}
                  </option>
                </select>
                <div
                  class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 320 512"
                    class="w-4 h-4"
                  >
                    <path
                      d="M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z"
                    />
                  </svg>
                </div>
              </div>
            </Field>
            <ErrorMessage name="location" class="text-sm text-red-500" />
          </div>
        </div>
      </div>

      <!-- Professional Background Section -->
      <div>
        <h4 class="text-xl font-medium md:text-2xl">Professional Background</h4>
        <div class="w-full h-[1px] bg-gray-200"></div>
        <div class="grid gap-4 mt-2">
          <!-- Years of experience -->
          <div class="flex flex-col w-full gap-y-1">
            <label
              class="font-medium text-[#7B7979] text-base sm:text-lg"
              for="yearsExperience"
            >
              Years of experience
            </label>
            <Field
              name="yearsExperience"
              v-slot="{ field, errors }"
              :validateOnInput="hasBeenSubmitted"
            >
              <input
                v-bind="field"
                type="number"
                placeholder="Insert years of experience..."
                :class="{ 'ring-red-500 border-red-500': errors[0] }"
                style="appearance: textfield"
                @keypress="numbersOnly"
                @paste="handleNumberPaste"
              />
            </Field>
            <ErrorMessage name="yearsExperience" class="text-sm text-red-500" />
          </div>

          <!-- Portfolio Links -->
          <div class="flex flex-col w-full gap-y-1">
            <label class="font-medium text-[#7B7979]">Portfolio Links</label>
            <div class="flex flex-col gap-y-5">
              <!-- Channel fields -->
              <FieldArray name="additionalChannels" v-slot="{ fields, push, remove }">
                <!-- Ensure at least one field is always visible -->
                <div class="flex flex-col gap-y-4">
                  <!-- First default field - always visible -->
                  <div class="flex flex-col gap-x-2">
                    <div class="grow">
                      <div class="flex-col md:flex-row flex md:gap-x-2 gap-y-2">
                        <div class="flex flex-col w-full md:max-w-[17rem]">
                          <Field
                            name="additionalChannels[0].type"
                            :validateOnInput="hasBeenSubmitted"
                            v-slot="{ field: fieldProps, errors }"
                          >
                            <div class="relative w-full">
                              <select
                                v-bind="fieldProps"
                                class="w-full appearance-none text-ellipsis whitespace-nowrap overflow-hidden"
                                style="padding-right: 2rem"
                                :class="{ 'ring-red-500 border-red-500': errors[0] }"
                                :disabled="loading"
                              >
                                <option value="">Select channel type</option>
                                <option
                                  v-for="channel in availableChannels(fields, 0)"
                                  :key="channel.value"
                                  :value="channel.value"
                                >
                                  {{ channel.label }}
                                </option>
                              </select>
                              <div
                                class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 320 512"
                                  class="w-4 h-4"
                                >
                                  <path
                                    d="M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z"
                                  />
                                </svg>
                              </div>
                            </div>
                          </Field>
                          <ErrorMessage
                            name="additionalChannels[0].type"
                            class="text-sm text-red-500"
                          />
                        </div>
                        <div class="flex flex-col w-full h-fit">
                          <Field
                            name="additionalChannels[0].url"
                            :validateOnInput="hasBeenSubmitted"
                            v-slot="{ field: fieldProps, errors }"
                          >
                            <input
                              v-bind="fieldProps"
                              type="text"
                              placeholder="Insert channel URL"
                              class="grow"
                              :class="{ 'ring-red-500 border-red-500': errors[0] }"
                              :disabled="loading"
                            />
                          </Field>
                          <ErrorMessage
                            name="additionalChannels[0].url"
                            class="text-sm text-red-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Additional fields -->
                  <div
                    v-for="(field, index) in fields.slice(1)"
                    :key="field.key"
                    class="flex flex-col gap-x-2"
                  >
                    <div class="grow">
                      <div class="flex-col md:flex-row flex md:gap-x-2 gap-y-2">
                        <div class="flex flex-col w-full md:max-w-[17rem]">
                          <Field
                            :name="`additionalChannels[${index + 1}].type`"
                            :validateOnInput="hasBeenSubmitted"
                            v-slot="{ field: fieldProps, errors }"
                          >
                            <div class="relative w-full">
                              <select
                                v-bind="fieldProps"
                                class="w-full appearance-none pr-8"
                                :class="{ 'ring-red-500 border-red-500': errors[0] }"
                                :disabled="loading"
                              >
                                <option value="">Select channel type</option>
                                <option
                                  v-for="channel in availableChannels(fields, index + 1)"
                                  :key="channel.value"
                                  :value="channel.value"
                                >
                                  {{ channel.label }}
                                </option>
                              </select>
                              <div
                                class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 320 512"
                                  class="w-4 h-4"
                                >
                                  <path
                                    d="M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z"
                                  />
                                </svg>
                              </div>
                            </div>
                          </Field>
                          <ErrorMessage
                            :name="`additionalChannels[${index + 1}].type`"
                            class="text-sm text-red-500"
                          />
                        </div>
                        <div class="flex flex-col w-full h-fit">
                          <div class="flex gap-x-2">
                            <Field
                              :name="`additionalChannels[${index + 1}].url`"
                              :validateOnInput="hasBeenSubmitted"
                              v-slot="{ field: fieldProps, errors }"
                            >
                              <input
                                v-bind="fieldProps"
                                type="text"
                                placeholder="Insert channel URL"
                                class="grow"
                                :class="{ 'ring-red-500 border-red-500': errors[0] }"
                                :disabled="loading"
                              />
                            </Field>
                            <button
                              type="button"
                              class="btn-form"
                              @click="() => remove(index + 1)"
                              :disabled="loading"
                            >
                              -
                            </button>
                          </div>
                          <ErrorMessage
                            :name="`additionalChannels[${index + 1}].url`"
                            class="text-sm text-red-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Add new channel button -->
                  <button
                    v-if="fields.length < availableChannelTypes.length"
                    type="button"
                    class="w-full btn-form self-start py-2"
                    @click="() => push({ type: '', url: '' })"
                    :disabled="fields.length >= availableChannelTypes.length || loading"
                  >
                    + Add an additional channel field
                  </button>
                </div>
              </FieldArray>
            </div>
          </div>
        </div>
      </div>

      <!-- Availability Section -->
      <div>
        <h4 class="text-xl font-medium md:text-2xl">Availability</h4>
        <div class="w-full h-[1px] bg-gray-200 mb-2"></div>
        <label class="font-medium text-[#7B7979]" for="availability"
          >Expected Monthly Hours</label
        >
        <div class="relative flex flex-col w-full mt-1">
          <Field
            name="availability"
            :validateOnInput="hasBeenSubmitted"
            v-slot="{ field, errors }"
          >
            <div class="relative w-full">
              <select
                v-bind="field"
                class="w-full appearance-none pr-8"
                :class="{ 'ring-red-500 border-red-500': errors[0] }"
              >
                <option value="">Select availability</option>
                <option value="10 hours">10 hours</option>
                <option value="10-20 hours">10-20 hours</option>
                <option value="20-30 hours">20-30 hours</option>
                <option value="30-40 hours">30-40 hours</option>
                <option value="40+ hours">40+ hours</option>
              </select>
              <div
                class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 320 512"
                  class="w-4 h-4"
                >
                  <path
                    d="M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z"
                  />
                </svg>
              </div>
            </div>
          </Field>
          <ErrorMessage name="availability" class="text-sm text-red-500" />
        </div>
      </div>
    </div>
    <div class="w-full flex justify-end py-8 px-2 sm:px-0">
      <button
        @click="hasBeenSubmitted = true"
        type="submit"
        class="btn-primary flex items-center gap-y-2"
        :disabled="loading"
      >
        Next
      </button>
    </div>
  </Form>

  <CodeOTP
    v-if="viewCodeOTP"
    :email="emailEntered"
    @next="onSubmit"
    @back="deleteSectionCodeOTP"
  />
  <MessageComponent />
</template>

<script setup>
import CodeOTP from "@/shared/component/CodeOTP.vue";
import { Field, Form, ErrorMessage, FieldArray } from "vee-validate";
import { defineProps, defineEmits, ref, onMounted } from "vue";
import {
  // phoneNumberOnly,
  numbersOnly,
  handleNumberPaste,
  trimEmailField,
  // handlePaste,
} from "@/shared/utils/helper.js";
import * as yup from "yup";
import axios from "axios";
import { useMessage } from "@/shared/services/messageServices";
import MessageComponent from "@/shared/component/MessageComponent.vue";
import { countries } from "@/shared/utils/countries.js";
// import { phonePrefixes } from "@/shared/utils/prefix-phone.js";
import IntlTelInput from "intl-tel-input/vueWithUtils";
import "intl-tel-input/styles";

const messagesService = useMessage();
const props = defineProps({
  initialValues: Object,
});

const loading = ref(false);

const hasBeenSubmitted = ref(false);
const viewCodeOTP = ref(false);
const emailEntered = ref("");
const valuesForm = ref({});

const emit = defineEmits(["next"]);

const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-?=&]*)*\/?$/;

const phoneNumber = ref("");
const isPhoneValid = ref(false);
const phoneErrorCode = ref(null);

function getPhoneErrorMessage() {
  if (!phoneNumber.value) return "Contact number is required";
  if (!isPhoneValid.value) return "Please enter a valid phone number";
  return "";
}

// Update schema to remove phone validation (handled by IntlTelInput)
const schema = yup.object().shape({
  fullName: yup
    .string()
    .required("Full name is required")
    .min(2, "Name must be at least 2 characters"),
  email: yup.string().required("Email is required").email("Invalid email format"),
  location: yup.string().required("Location is required"),
  yearsExperience: yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .required("Years of experience is required")
    .min(0, "Years must be positive")
    .typeError("Must be a number"),
  additionalChannels: yup
    .array()
    .of(
      yup.object().shape({
        type: yup.string().required("The channel type was not selected"),
        url: yup
          .string()
          .required("URL is required")
          .matches(urlRegex, "Must be a valid URL"),
      })
    )
    .required()
    .min(1, "At least one portfolio channel is required"),
  availability: yup.string(),
});

const availableChannelTypes = [
  { value: "behanceChannel", label: "Behance Channel" },
  { value: "pinterestChannel", label: "Pinterest Channel" },
  { value: "personalWebsite", label: "Personal Website" },
  { value: "facebook", label: "Facebook" },
  { value: "instagram", label: "Instagram" },
];

function availableChannels(fields, currentIndex) {
  const selectedTypes = fields
    .map((field, idx) => (idx !== currentIndex ? field.value?.type : null))
    .filter((type) => type !== null);

  return availableChannelTypes.filter(
    (channel) => !selectedTypes.includes(channel.value)
  );
}

async function verifyEmail(email) {
  loading.value = true;
  if (props.initialValues.email != emailEntered.value) {
    try {
      await axios.post(`${process.env.VUE_APP_ENDPOINT_API}/work-with-us/verify-form`, {
        email: email,
        role: props.initialValues.role,
      });
      return true;
    } catch (error) {
      messagesService.error(
        "An email is already in our platform. Please try with another email"
      );
      return false;
    } finally {
      loading.value = false;
    }
  }
}

async function handleForm(values) {
  // Check phone validity
  if (!phoneNumber.value || !isPhoneValid.value) {
    hasBeenSubmitted.value = true; // Trigger validation display
    return;
  }

  emailEntered.value = values.email;
  const transformedValues = { ...values };

  // Add phone number from IntlTelInput
  transformedValues.contactNumber = phoneNumber.value;

  const isEmailPresent = await verifyEmail(values.email);
  if (!isEmailPresent) {
    return;
  }

  if (transformedValues.additionalChannels?.length > 0) {
    transformedValues.additionalChannels.forEach((channel) => {
      if (channel.type && channel.url) {
        transformedValues[channel.type] = channel.url;
      }
    });
    delete transformedValues.additionalChannels;
  }

  delete valuesForm.value.email;
  valuesForm.value = transformedValues;
  viewSectionCodeOTP(transformedValues);
}

function viewSectionCodeOTP() {
  if (props.initialValues.email != emailEntered.value) {
    viewCodeOTP.value = true;
  } else {
    emit("next", valuesForm.value);
  }
}

function deleteSectionCodeOTP() {
  viewCodeOTP.value = false;
}

function onSubmit() {
  valuesForm.value.email = emailEntered.value;
  emit("next", valuesForm.value);
}

const formRef = ref(null);

onMounted(() => {
  window.scrollTo({ top: 1, behavior: "smooth" });
});
</script>
